export interface AvatarProps {
  src?: string;
  alt?: string;
  initials?: string;
  emoji?: string;
  /** Background color for emoji avatars (hex, rgb, etc.). */
  color?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  /**
   * Render a neutral pulsing placeholder at the configured size. Used by
   * <ProfileAvatar> while a signed URL is resolving, so an expected image
   * doesn't flash the emoji fallback before it loads.
   */
  loading?: boolean;
}

// NOTE: Avatar stays a dumb primitive that only knows about `src`. Callers
// that need to resolve a storage path to a signed URL should use
// <ProfileAvatar> (components/domain/shared/ProfileAvatar), which wraps
// Avatar and handles the useAvatarImageUrl hook.

const sizeStyles: Record<string, string> = {
  // `xs` exists for inline chips/pills (ChildFilterPills), where an `sm`
  // avatar would grow the pill well past its 36px Figma height.
  xs: 'w-6 h-6 text-sm leading-4',
  sm: 'w-8 h-8 text-sm leading-5',
  md: 'w-10 h-10 text-base leading-6',
  lg: 'w-12 h-12 text-lg leading-7',
  xl: 'w-[100px] h-[100px] text-4xl',
  '2xl': 'w-[160px] h-[160px] text-6xl',
};

export function Avatar({
  src,
  alt = '',
  initials,
  emoji,
  color,
  size = 'md',
  className = '',
  loading = false,
}: AvatarProps) {
  // Sizing + shape — always applied. `inline-flex` is needed even for
  // the <img> variant so that the wrapping <span> accepts the explicit
  // w/h from sizeStyles. Without a non-inline display the span collapses
  // to 0x0 and the <img> child falls back to its intrinsic size (photo
  // avatars rendered full-resolution instead of as a circle).
  const shapeClasses = [
    'inline-flex rounded-full shrink-0 border border-border overflow-hidden',
    sizeStyles[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Emoji/initials variants additionally center the content inside the
  // circle.
  const contentClasses = [shapeClasses, 'items-center justify-center'].join(
    ' ',
  );

  if (loading) {
    return (
      <span
        className={[shapeClasses, 'bg-surface-muted animate-pulse'].join(' ')}
        aria-label={alt}
        aria-busy="true"
      />
    );
  }

  if (src) {
    return (
      <span
        className={shapeClasses}
        aria-label={alt || undefined}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover block"
        />
      </span>
    );
  }

  if (emoji) {
    const hasBgClass = className.includes('bg-');
    return (
      <span
        className={[
          contentClasses,
          hasBgClass || color ? '' : 'bg-surface-secondary',
        ]
          .filter(Boolean)
          .join(' ')}
        style={color ? { backgroundColor: color } : undefined}
        role="img"
        aria-label={alt}
      >
        {emoji}
      </span>
    );
  }

  const displayInitials = initials ?? '?';
  return (
    <span
      className={[
        contentClasses,
        'bg-primary text-primary-foreground font-semibold border-primary',
      ].join(' ')}
      aria-label={alt || displayInitials}
    >
      {displayInitials}
    </span>
  );
}
